/**
 * Vaccine due dates, shared with the phone app. The same rules as the
 * website's Vaccines tab (apps/web/src/legacy/overrides/vaccines.js);
 * test/vaccines.test.ts runs both and checks they agree.
 *
 * Records (table "vaccinations", key mpc_vaccines_v1::<personId>):
 *   { id, vaccine, dose, status: 'given'|'planned'|'skipped', date, place, notes }
 *   { id, kind: 'plan', vaccine, start, off }
 *   { id, kind: 'custom', name, status, date, place, notes }
 */
import { addDays, formatDateISO, parseDateISO } from './index';

export interface Period { y?: number; m?: number; w?: number; d?: number }
export interface VaccineDose { label: string; at?: Period; gap?: Period }
export interface Vaccine {
  id: string; group: 'child' | 'women' | 'adult'; name: string; protects: string; optional?: boolean;
  sex?: string; fromAge?: number; untilAge?: number; repeat?: Period; doses: VaccineDose[];
}
export interface VaccineRecord {
  id: string; kind?: 'plan' | 'custom'; vaccine?: string; dose?: number; status?: 'given' | 'planned' | 'skipped';
  date?: string; start?: string; off?: boolean; name?: string; [extra: string]: unknown;
}
export interface VaccinePerson { id: string; name?: string; dob?: string; age?: string; gender?: string; [extra: string]: unknown }
export type VaccineStatus = 'given' | 'skipped' | 'waiting' | 'nodate' | 'overdue' | 'due' | 'upcoming';
export interface VaccineDue { key: string; name: string; label: string; due: string; status: VaccineStatus }

export const VACCINE_SOON_DAYS = 30;

export const VACCINES: Vaccine[] = [
  { id: 'bcg', group: 'child', name: 'BCG', protects: 'Tuberculosis', doses: [{ label: 'At birth', at: { d: 0 } }] },
  { id: 'penta', group: 'child', name: 'Pentavalent (DPT-HepB-Hib)', protects: 'Diphtheria, whooping cough, tetanus, hepatitis B, Hib',
    doses: [{ label: 'Dose 1', at: { w: 6 } }, { label: 'Dose 2', at: { w: 10 }, gap: { w: 4 } }, { label: 'Dose 3', at: { w: 14 }, gap: { w: 4 } }] },
  { id: 'opv', group: 'child', name: 'OPV (oral polio)', protects: 'Polio',
    doses: [{ label: 'Dose 1', at: { w: 6 } }, { label: 'Dose 2', at: { w: 10 }, gap: { w: 4 } }, { label: 'Dose 3', at: { w: 14 }, gap: { w: 4 } }] },
  { id: 'pcv', group: 'child', name: 'PCV (pneumococcal)', protects: 'Pneumonia, meningitis',
    doses: [{ label: 'Dose 1', at: { w: 6 } }, { label: 'Dose 2', at: { w: 10 }, gap: { w: 4 } }, { label: 'Dose 3', at: { w: 18 }, gap: { w: 8 } }] },
  { id: 'fipv', group: 'child', name: 'fIPV (injectable polio)', protects: 'Polio',
    doses: [{ label: 'Dose 1', at: { w: 6 } }, { label: 'Dose 2', at: { w: 14 }, gap: { w: 8 } }] },
  { id: 'mr', group: 'child', name: 'MR (measles-rubella)', protects: 'Measles, rubella',
    doses: [{ label: 'Dose 1', at: { m: 9 } }, { label: 'Dose 2', at: { m: 15 }, gap: { w: 4 } }] },
  { id: 'tcv', group: 'child', name: 'TCV (typhoid conjugate)', protects: 'Typhoid', doses: [{ label: 'Dose', at: { m: 15 } }] },
  { id: 'hpv', group: 'child', name: 'HPV', protects: 'Cervical cancer', sex: 'Female', fromAge: 9, untilAge: 15, doses: [{ label: 'Dose', at: { y: 10 } }] },
  { id: 'td', group: 'women', name: 'Td (tetanus-diphtheria)', protects: 'Tetanus (mother and newborn), diphtheria', sex: 'Female', fromAge: 14, untilAge: 50,
    doses: [{ label: 'Td 1', at: { y: 15 } }, { label: 'Td 2', gap: { w: 4 } }, { label: 'Td 3', gap: { m: 6 } }, { label: 'Td 4', gap: { y: 1 } }, { label: 'Td 5', gap: { y: 1 } }] },
  { id: 'flu', group: 'adult', optional: true, name: 'Influenza (flu)', protects: 'Seasonal flu', repeat: { y: 1 }, doses: [{ label: 'This year' }] },
  { id: 'pneumo', group: 'adult', optional: true, name: 'Pneumococcal (adult)', protects: 'Pneumonia, meningitis', doses: [{ label: 'Dose' }] },
  { id: 'hepb', group: 'adult', optional: true, name: 'Hepatitis B (adult)', protects: 'Hepatitis B',
    doses: [{ label: 'Dose 1' }, { label: 'Dose 2', gap: { m: 1 } }, { label: 'Dose 3', gap: { m: 5 } }] },
  { id: 'td10', group: 'adult', optional: true, name: 'Td / Tdap booster', protects: 'Tetanus, diphtheria (whooping cough with Tdap)', repeat: { y: 10 }, doses: [{ label: 'Booster' }] },
  { id: 'zoster', group: 'adult', optional: true, name: 'Shingles (zoster)', protects: 'Shingles', doses: [{ label: 'Dose 1' }, { label: 'Dose 2', gap: { m: 2 } }] },
  { id: 'typhoid', group: 'adult', optional: true, name: 'Typhoid (adult)', protects: 'Typhoid', repeat: { y: 3 }, doses: [{ label: 'Dose' }] },
  { id: 'hepa', group: 'adult', optional: true, name: 'Hepatitis A', protects: 'Hepatitis A', doses: [{ label: 'Dose 1' }, { label: 'Dose 2', gap: { m: 6 } }] },
  { id: 'covid', group: 'adult', optional: true, name: 'COVID-19', protects: 'COVID-19', doses: [{ label: 'Dose' }] },
];

function daysInMonth(y: number, m: number) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }
function addMonthsISO(iso: string, k: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const total = (m - 1) + k;
  const ty = y + Math.floor(total / 12);
  const tm = ((total % 12) + 12) % 12 + 1;
  return `${ty}-${String(tm).padStart(2, '0')}-${String(Math.min(d, daysInMonth(ty, tm))).padStart(2, '0')}`;
}
export function addPeriod(iso: string, p?: Period | null): string {
  if (!iso || !p) return iso;
  let s = iso;
  if (p.y) s = addMonthsISO(s, 12 * p.y);
  if (p.m) s = addMonthsISO(s, p.m);
  const days = (p.w || 0) * 7 + (p.d || 0);
  if (days) s = formatDateISO(addDays(parseDateISO(s)!, days));
  return s;
}

/** Age in years from the birth date, or from the typed age; null if unknown. */
export function ageYears(p: VaccinePerson, todayISO: string): number | null {
  const dob = parseDateISO(p.dob || ''), today = parseDateISO(todayISO)!;
  if (dob && dob <= today) return Math.round((today.getTime() - dob.getTime()) / 86400000) / 365.25;
  const s = String(p.age || '').trim().toLowerCase();
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return null;
  if (/month|mo\b|m$/.test(s)) return n / 12;
  if (/week|wk|w$/.test(s)) return n / 52;
  if (/day|d$/.test(s)) return n / 365;
  return n;
}

function defaultInPlan(v: Vaccine, p: VaccinePerson, todayISO: string): boolean {
  if (v.optional) return false;
  if (v.sex && p.gender && p.gender !== v.sex) return false;
  const age = ageYears(p, todayISO);
  if (age == null) return v.group === 'child' && !v.sex ? true : !v.sex || p.gender === v.sex;
  if (v.fromAge != null && age < v.fromAge - 1 && v.group !== 'child') return false;
  if (v.untilAge != null) return age < v.untilAge;
  return age < 18;
}
export function vaccineInPlan(v: Vaccine, p: VaccinePerson, recs: VaccineRecord[], todayISO: string): boolean {
  const plan = recs.find(r => r.kind === 'plan' && r.vaccine === v.id);
  if (plan) return !plan.off;
  if (recs.some(r => !r.kind && r.vaccine === v.id)) return true;
  return defaultInPlan(v, p, todayISO);
}

export interface VaccineRow { vaccine: string; dose: number; label: string; due: string | null; status: VaccineStatus }
export function vaccineRows(v: Vaccine, p: VaccinePerson, recs: VaccineRecord[], todayISO: string): VaccineRow[] {
  const soon = addPeriod(todayISO, { d: VACCINE_SOON_DAYS });
  const plan = recs.find(r => r.kind === 'plan' && r.vaccine === v.id);
  const doseRecs = recs.filter(r => !r.kind && r.vaccine === v.id);
  const resolvedMax = doseRecs.filter(r => r.status === 'given' || r.status === 'skipped').reduce((m, r) => Math.max(m, r.dose || 0), 0);
  const total = v.repeat ? Math.max(v.doses.length, resolvedMax + 1) : v.doses.length;
  const rows: VaccineRow[] = [];
  let prev: { status: VaccineStatus; date: string; due: string | null } | null = null;
  for (let i = 1; i <= total; i++) {
    const spec: VaccineDose = i <= v.doses.length ? v.doses[i - 1] : { label: `Dose ${i}`, gap: v.repeat };
    const gap = spec.gap || (i > 1 && v.repeat ? v.repeat : null);
    const rec = doseRecs.find(r => r.dose === i) || null;
    const cands: string[] = [];
    if (spec.at && p.dob) cands.push(addPeriod(p.dob, spec.at));
    let waiting = false;
    if (gap && prev) {
      if (prev.status === 'given') { if (prev.date) cands.push(addPeriod(prev.date, gap)); }
      else if (prev.status === 'skipped') cands.push(addPeriod(prev.due || todayISO, gap));
      else { if (prev.due) cands.push(addPeriod(prev.due, gap)); if (!spec.at) waiting = true; }
    }
    if (i === 1 && !spec.at && plan && plan.start) cands.push(plan.start);
    let due: string | null = cands.length ? cands.sort().slice(-1)[0] : null;
    if (rec && rec.status === 'planned' && rec.date) { due = rec.date; waiting = false; }
    let status: VaccineStatus;
    if (rec && rec.status === 'given') status = 'given';
    else if (rec && rec.status === 'skipped') status = 'skipped';
    else if (waiting) status = 'waiting';
    else if (!due) status = 'nodate';
    else if (due < todayISO) status = 'overdue';
    else if (due <= soon) status = 'due';
    else status = 'upcoming';
    rows.push({ vaccine: v.id, dose: i, label: spec.label || `Dose ${i}`, due, status });
    prev = { status, date: rec ? String(rec.date || '') : '', due };
  }
  return rows;
}

/**
 * The next dose of each planned vaccine (and each other vaccine) that has a
 * due date and is not given or skipped: overdue, due soon or upcoming.
 * `withinDays` limits it to due dates up to that many days from today.
 */
export function vaccinesDue(p: VaccinePerson, recs: VaccineRecord[], todayISO: string, withinDays = VACCINE_SOON_DAYS): VaccineDue[] {
  const until = addPeriod(todayISO, { d: withinDays });
  const out: VaccineDue[] = [];
  for (const v of VACCINES) {
    if (!vaccineInPlan(v, p, recs, todayISO)) continue;
    const next = vaccineRows(v, p, recs, todayISO).find(r => r.status === 'overdue' || r.status === 'due' || r.status === 'upcoming');
    if (next && next.due && next.due <= until) out.push({ key: `${v.id}#${next.dose}`, name: v.name, label: next.label, due: next.due, status: next.status });
  }
  for (const c of recs.filter(r => r.kind === 'custom')) {
    if (c.status === 'given' || c.status === 'skipped' || !c.date || c.date > until) continue;
    out.push({ key: `custom#${c.id}`, name: c.name || 'Vaccine', label: '', due: c.date, status: c.date < todayISO ? 'overdue' : 'due' });
  }
  return out.sort((a, b) => a.due.localeCompare(b.due));
}
